import React, { useMemo } from "react";
import { Box, Stack } from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import Heading3 from "../../../typography/heading-03";
import Body2 from "../../../typography/body-02";
import LoadingDataModal from "../../../modal/loading-data-modal";
import styles from "./index.module.scss";

const normalizeNumber = (value) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const addCounts = (acc, next = {}) => {
	acc.total += normalizeNumber(next.total);
	acc.openRequested += normalizeNumber(next.openRequested);
	acc.fulfilled += normalizeNumber(next.fulfilled);
	acc.waived += normalizeNumber(next.waived);
	return acc;
};

const extractSummaryCounts = (documentListCount) => {
	const summary = {
		total: 0,
		openRequested: 0,
		fulfilled: 0,
		waived: 0,
	};

	if (Array.isArray(documentListCount)) {
		documentListCount.forEach((item) => {
			if (!item || typeof item !== "object") return;

			const openRequested = normalizeNumber(
				item?.openRequested ?? item?.open ?? item?.requested
			);
			const fulfilled = normalizeNumber(item?.fulfilled ?? item?.closed);
			const waived = normalizeNumber(item?.waived);
			const total =
				normalizeNumber(item?.total) || openRequested + fulfilled + waived;

			addCounts(summary, {
				total,
				openRequested,
				fulfilled,
				waived,
			});
		});

		return summary;
	}

	if (documentListCount && typeof documentListCount === "object") {
		const topLevelKeys = [
			"total",
			"openRequested",
			"fulfilled",
			"closed",
			"waived",
		];
		const hasTopLevelCounts = topLevelKeys.some(
			(key) => Object.prototype.hasOwnProperty.call(documentListCount, key)
		);

		if (hasTopLevelCounts) {
			const openRequested = normalizeNumber(
				documentListCount?.openRequested ?? documentListCount?.open
			);
			const fulfilled = normalizeNumber(
				documentListCount?.fulfilled ?? documentListCount?.closed
			);
			const waived = normalizeNumber(documentListCount?.waived);
			const total =
				normalizeNumber(documentListCount?.total) ||
				openRequested + fulfilled + waived;

			return addCounts(summary, {
				total,
				openRequested,
				fulfilled,
				waived,
			});
		}

		Object.values(documentListCount).forEach((value) => {
			if (!value || typeof value !== "object" || Array.isArray(value)) return;

			const openRequested = normalizeNumber(
				value?.openRequested ?? value?.open ?? value?.requested
			);
			const fulfilled = normalizeNumber(value?.fulfilled ?? value?.closed);
			const waived = normalizeNumber(value?.waived);
			const total =
				normalizeNumber(value?.total) || openRequested + fulfilled + waived;

			addCounts(summary, {
				total,
				openRequested,
				fulfilled,
				waived,
			});
		});
	}

	return summary;
};

const EnhancedConditionDocumentsListCountTable = ({
	documentListCount = {},
	isLoading = false,
}) => {
	const summaryCounts = useMemo(
		() => extractSummaryCounts(documentListCount),
		[documentListCount]
	);

	const cards = [
		{
			key: "total",
			label: "Total Conditions",
			value: summaryCounts.total,
			icon: AssignmentOutlinedIcon,
			className: styles.summaryCardTotal,
		},
		{
			key: "openRequested",
			label: "Open",
			value: summaryCounts.openRequested,
			icon: InfoOutlinedIcon,
			className: styles.summaryCardOpen,
		},
		{
			key: "fulfilled",
			label: "Closed",
			value: summaryCounts.fulfilled,
			icon: TaskAltOutlinedIcon,
			className: styles.summaryCardFulfilled,
		},
	];

	return (
		<Stack direction="column" className={styles.summarySection}>
			<LoadingDataModal
				open={isLoading}
				text="Setting up the conditions list"
			/>

			<Stack direction="row" flexWrap="wrap" className={styles.summaryContainer}>
				{cards.map((card) => {
					const SummaryIcon = card.icon;

					return (
						<Box
							key={card.key}
							className={`${styles.summaryCard} ${card.className}`}
						>
							<Box className={styles.summaryIconWrap}>
								<SummaryIcon className={styles.summaryIcon} />
							</Box>
							<Stack direction="column" className={styles.summaryTextWrap}>
								<Heading3
									text={String(card.value)}
									extraClass={styles.summaryValue}
									fontType="bold"
								/>
								<Body2
									text={card.label}
									extraClass={styles.summaryLabel}
									fontType="semibold"
								/>
							</Stack>
						</Box>
					);
				})}
			</Stack>
		</Stack>
	);
};

export default EnhancedConditionDocumentsListCountTable;
